import { memo, useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, TextField, InputAdornment, Popover, List, ListItem, IconButton } from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { GridItem, Button } from '../../../../components/shared';
import { LABELS } from '../../constants';
import ImmediateCloseTooltip from '../ImmediateCloseTooltip';
import useInfiniteScrollItems from '../../hooks/useInfiniteScrollItems';
import useResettableStateSetter from '../../hooks/useResettableStateSetter';
import { sortSublocationsByName } from '../../utils/sortSublocations';

const displayName = 'SelectedSublocationsPopover';

const SelectedSublocationsPopover = memo(
  ({ selectedItems = [], unselectedItems = [], summaryTitle, summaryDetails, onRemove, classes }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverWidth, setPopoverWidth] = useState(undefined);
    const [activeView, setActiveView] = useState('selected');
    const [popoverSearchTerm, setPopoverSearchTerm] = useState('');
    const isOpen = Boolean(anchorEl);

    const selectedCount = selectedItems.length;
    const unselectedCount = unselectedItems.length;
    const isSelectedView = activeView === 'selected';
    const items = useMemo(
      () => sortSublocationsByName(isSelectedView ? selectedItems : unselectedItems),
      [isSelectedView, selectedItems, unselectedItems],
    );
    const filteredItems = useMemo(() => {
      const searchValue = popoverSearchTerm.trim().toLowerCase();
      if (!searchValue) return items;
      return items.filter((item) =>
        String(item?.name ?? '')
          .toLowerCase()
          .includes(searchValue),
      );
    }, [items, popoverSearchTerm]);
    const {
      visibleItemsCount,
      resetVisibleItems,
      handleScroll: handlePopoverScroll,
    } = useInfiniteScrollItems({
      totalItems: filteredItems.length,
    });
    const visibleFilteredItems = useMemo(
      () => filteredItems.slice(0, visibleItemsCount),
      [filteredItems, visibleItemsCount],
    );

    useEffect(() => {
      if (!isOpen) return;
      resetVisibleItems();
    }, [isOpen, activeView, popoverSearchTerm, resetVisibleItems]);

    const handleOpen = useCallback(
      (event) => {
        const triggerWidth = event.currentTarget?.getBoundingClientRect?.().width;
        setPopoverWidth(triggerWidth && Number.isFinite(triggerWidth) ? triggerWidth : undefined);
        setAnchorEl(event.currentTarget);
        setActiveView('selected');
        setPopoverSearchTerm('');
        resetVisibleItems();
      },
      [resetVisibleItems],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleActiveViewChange = useResettableStateSetter(setActiveView, resetVisibleItems);

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleOpen(event);
        } else if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          handleClose();
        }
      },
      [handleOpen, handleClose, isOpen],
    );

    return (
      <GridItem className={classes.selectedPopoverContainer}>
        <Typography variant="subtitle2" className={classes.fieldLabel}>
          Selected Sublocations
        </Typography>
        <button
          type="button"
          tabIndex={0}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          className={classes.selectedPopoverTrigger}
        >
          <Typography variant="body2" className={classes.selectedPopoverTriggerTitle}>
            {summaryTitle}
          </Typography>
          {summaryDetails ? (
            <Typography variant="caption" className={classes.selectedPopoverTriggerHint}>
              {summaryDetails}
            </Typography>
          ) : null}
        </button>
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            className: classes.selectedPopoverPaper,
            sx: {
              width: popoverWidth ? `${popoverWidth}px` : undefined,
              minWidth: popoverWidth ? `${popoverWidth}px` : undefined,
              maxWidth: 'none',
              boxSizing: 'border-box',
            },
          }}
        >
          <Box className={classes.selectedPopoverHeader}>
            <Typography variant="subtitle2" className={classes.selectedPopoverHeaderTitle}>
              Sublocations
            </Typography>
            <Typography variant="caption" className={classes.selectedPopoverHeaderCaption}>
              Last posting end date shown at right
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant={isSelectedView ? 'contained' : 'outlined'}
                onClick={() => handleActiveViewChange('selected')}
                className={classes.sublocationButton}
              >
                {`Selected (${selectedCount})`}
              </Button>
              <Button
                size="small"
                variant={isSelectedView ? 'outlined' : 'contained'}
                onClick={() => handleActiveViewChange('unselected')}
                className={classes.sublocationButton}
              >
                {`Unselected (${unselectedCount})`}
              </Button>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder={LABELS.SEARCH_SUBLOCATIONS}
              value={popoverSearchTerm}
              onChange={(event) => {
                setPopoverSearchTerm(event.target.value);
                resetVisibleItems();
              }}
              sx={{ mt: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List disablePadding sx={{ py: 0.5 }} className={classes.selectedPopoverList} onScroll={handlePopoverScroll}>
            {visibleFilteredItems.length === 0 ? (
              <ListItem className={classes.selectedPopoverRow}>
                <Typography variant="body2" className={classes.selectedPopoverRowName}>
                  {popoverSearchTerm
                    ? 'No matching sublocations'
                    : isSelectedView
                    ? 'No selected sublocations'
                    : 'No unselected sublocations'}
                </Typography>
              </ListItem>
            ) : (
              visibleFilteredItems.map((item) => (
                <ListItem key={item.id} className={classes.selectedPopoverRow}>
                  <Box className={classes.selectedPopoverRowMain}>
                    <Box className={classes.selectedPopoverRowContent}>
                      <Typography variant="body2" className={classes.selectedPopoverRowName}>
                        <ImmediateCloseTooltip title={item.name}>
                          <span className={classes.selectedPopoverRowNameText}>{item.name}</span>
                        </ImmediateCloseTooltip>
                      </Typography>
                      {item.endDate ? (
                        <Typography variant="caption" className={classes.selectedPopoverRowDate}>
                          {item.endDate}
                        </Typography>
                      ) : null}
                    </Box>
                    {isSelectedView ? (
                      <IconButton
                        size="small"
                        className={classes.selectedPopoverRemoveButton}
                        aria-label={`Remove ${item.name}`}
                        onClick={() => onRemove?.(item.value)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        className={classes.selectedPopoverAddButton}
                        aria-label={`Add ${item.name}`}
                        onClick={() => onRemove?.(item.value)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </Popover>
      </GridItem>
    );
  },
);

SelectedSublocationsPopover.displayName = displayName;

export default SelectedSublocationsPopover;
